import React, { useLayoutEffect, useRef, useEffect, useState, useMemo } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5core from '@amcharts/amcharts5';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import './OilGasChart.scss';
import FullScreenIcon from '../../features/common/FullScreenIcon/FullScreenIcon';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import { exportChart } from '../../utilities/ExportChart';
import DownloadChart from '../time-series-chart/download/downloadChart';
import EmptyState from '../empty-state/EmptyState';
import { useAppSelector } from '../../hooks/storeHooks';
import {
  createChartLegend,
  createCursorBullet,
  createChartTooltip,
  customizeAxisLabels,
  setupCursor,
  setupCursorEvents,
} from '../../utilities/chartHelpers';
import Loader from '../../features/common/Loader/Loader';
import { R, useRBAC } from '../../rbac';
import { showErrorToast } from '../toast/toastBridge';
import { ProtectAction } from '../../rbac';
import useConfig from '../../hooks/useConfig';

interface WellTest {
  Date: string;
  Timestamp: number;
  Oil: number;
  Water: number;
  Gas: number;
  TotalFluid: number;
  BottomHoleFlowingPressure: number;
  GasLiquidRatio: number;
}

// Series configuration
export const OGL_SERIES_CONFIG = [
  {
    name: 'Oil (BOPD)',
    field: 'Oil',
    color: '#237E16',
    testColor: '#4294FF',
    visible: true,
    tooltipLabel: 'Oil',
    units: 'BOPD',
    splitView: true,
  },
  {
    name: 'Gas (MSCFD)',
    field: 'Gas',
    color: '#FA0022',
    testColor: '#32D583',
    visible: true,
    tooltipLabel: 'Gas',
    units: 'MSCFD',
    splitView: true,
  },
  {
    name: 'Water (BWPD)',
    field: 'Water',
    color: '#1DB7DF',
    testColor: '#FB6FE9',
    visible: true,
    tooltipLabel: 'Water',
    units: 'BWPD',
    splitView: true,
  },
  {
    name: 'Total liquids (STB/D)',
    field: 'TotalFluid',
    color: '#CD71F7',
    testColor: '#C471F3',
    visible: true,
    tooltipLabel: 'Total liquids',
    units: 'STB/D',
    splitView: false,
  },
  {
    name: 'GLR',
    field: 'GasLiquidRatio',
    color: '#FFCD02',
    testColor: '#FFCD02',
    visible: false,
    tooltipLabel: 'GLR',
    units: '',
    splitView: false,
  },
  {
    name: 'BHP (PSIG)',
    field: 'BottomHoleFlowingPressure',
    color: '#FF5E7A',
    testColor: '#FF5E7A',
    visible: false,
    tooltipLabel: 'BHP',
    units: 'PSIG',
    splitView: false,
  },
  {
    name: 'Gauge pressure (PSIG)',
    field: 'IntakePressure',
    color: '#F97066',
    testColor: '#F97066',
    visible: false,
    tooltipLabel: 'Gauge pressure',
    units: 'PSIG',
    splitView: false,
  },
];

interface OilGasChartProps {
  widgetName: 'oilWaterGasChart' | 'wellTestChart';
}

const OilGasChart: React.FC<OilGasChartProps> = ({ widgetName }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const startDate = useSelector((state: RootState) => state.AssetTrendDataSlice.startDate);
  const endDate = useSelector((state: RootState) => state.AssetTrendDataSlice.endDate);
  const dashboardName = useAppSelector((state) => state.AssetAnalysis.chartType);
  const rawWellTestData = useSelector((state: RootState) => state.WellTestChartDataSlice.data);
  const t1RawDefaultWellTestData = useSelector((state: RootState) => state.DefaultWellTest?.data);

  const chartViewOptions = useAppSelector((state: any) => state.AssetTrendDataSlice.chartViewOptions);
  const wellTestDataLoading = useSelector((state: RootState) => state.WellTestChartDataSlice.loading);
  const isTestDashboard = dashboardName?.toLowerCase().includes('tests');
  const isRLDashboard = dashboardName?.toLowerCase().includes('rl');
  const { enablePumpChecker } = useConfig();
  const effectivePumpChecker = enablePumpChecker && !isRLDashboard;

  const { showTooltip, isLogarithmic } = chartViewOptions || {};

  const resource = R.widget(dashboardName, widgetName);
  const { can } = useRBAC();
  const ACTION_KEYS = {
    oilWaterGasChart: {
      exportPdf: 'exportPdf',
      exportJpg: 'exportJpg',
      expand: 'expand',
    },
    wellTestChart: {
      exportPdf: 'ogwtChartExportPdf',
      exportJpg: 'ogwtChartExportJpg',
      expand: 'ogwtChartExpand',
    },
  };
  const rPermission = {
    canExportPdf: can(resource, ACTION_KEYS[widgetName].exportPdf),
    canExportJpg: can(resource, ACTION_KEYS[widgetName].exportJpg),
    canExpand: can(resource, ACTION_KEYS[widgetName].expand),
  };

  const wellTestDataAll: WellTest[] = useMemo(() => {
    const sourceData = effectivePumpChecker
      ? rawWellTestData // ESP / Pump Checker
      : t1RawDefaultWellTestData?.values || []; // Rod Lift / Default

    if (!Array.isArray(sourceData) || sourceData.length === 0) {
      return [];
    }

    return sourceData.map((item: any) => {
      const date = effectivePumpChecker ? item.Date : item.TestDate;
      const timestamp = item.Timestamp ?? (date ? new Date(date).getTime() : undefined);

      return {
        Date: date,
        Timestamp: timestamp,
        Oil: Number(item.Oil ?? 0),
        Water: Number(item.Water ?? 0),
        Gas: Number(item.Gas ?? 0),
        TotalFluid: Number(item.Oil ?? 0) + Number(item.Water ?? 0),
        BottomHoleFlowingPressure: Number(item.BottomHoleFlowingPressure ?? 0),
        GasLiquidRatio: Number(item.GasLiquidRatio ?? 0),
        IntakePressure: Number(item.IntakePressure ?? 0),
      };
    });
  }, [rawWellTestData, t1RawDefaultWellTestData, effectivePumpChecker]);

  const wellTestData = useMemo(() => {
    const startMs = startDate ? new Date(startDate).getTime() : undefined;
    const endMs = endDate ? new Date(endDate).getTime() : undefined;
    return wellTestDataAll.filter((item) => {
      const timestampMs = item.Timestamp;
      if (startMs && endMs) {
        return timestampMs >= startMs && timestampMs <= endMs;
      }
      return true;
    });
  }, [wellTestDataAll, startDate, endDate]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!rPermission.canExpand) {
      showErrorToast('Access Denied', 'You do not have permission to expand this chart.');
      return;
    }
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleChartDownload = async (option: string) => {
    const element = document.getElementById(`ogl-chart-content`);
    const filename = 'OilGasTotalLiquidsChart';
    if (!element) return;
    await exportChart(element, getExportType(option), filename, undefined, undefined, 'Oil, gas, water, total liquids');
  };

  const getExportType = (option: string): 'JPG' | 'PDF' => (option === 'Download as PDF' ? 'PDF' : 'JPG');

  // Tooltip creation
  const createTooltipFunction = (chart: am5xy.XYChart, root: am5.Root) => {
    if (isTestDashboard && !showTooltip) return undefined;

    if (isTestDashboard) {
      return createChartTooltip(chart, root, OGL_SERIES_CONFIG, true);
    } else {
      const tooltip = am5.Tooltip.new(root, {
        labelText: "{name} - {valueX.formatDate('MMM d, yyyy')} : {valueY}",
        pointerOrientation: 'right',
      });
      tooltip.get('background')?.setAll({
        strokeOpacity: 0,
        fillOpacity: 1,
      });
      return tooltip;
    }
  };

  // Create series helper
  const createSeries = (
    config: (typeof OGL_SERIES_CONFIG)[0],
    chart: am5xy.XYChart,
    xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
    yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
    root: am5.Root,
  ) => {
    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: config.name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: config.field,
        valueXField: 'Timestamp',
        stroke: isTestDashboard ? am5.color(config.testColor) : am5.color(config.color),
        fill: isTestDashboard ? am5.color(config.testColor) : am5.color(config.color),
        tooltip: createTooltipFunction(chart, root),
        visible: config.visible,
      }),
    );

    series.strokes.template.setAll({ strokeWidth: 2 });
    return series;
  };

  useLayoutEffect(() => {
    // Only create chart if we have data and a chart reference
    if (!chartRef.current || wellTestData.length === 0) return;

    // Create root element
    const root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Dark.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingLeft: 0,
        paddingRight: 30,
        paddingTop: 20,
        paddingBottom: 0,
        background: am5.Rectangle.new(root, {
          fill: am5.color('#122031'),
          fillOpacity: 0,
        }),
      }),
    );

    // Create axes
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: isTestDashboard ? 'minute' : 'day', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 80,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
      }),
    );

    //Force axis to use min/max
    if (startDate && endDate) {
      xAxis.setAll({
        min: new Date(startDate).getTime(),
        max: new Date(endDate).getTime(),
        strictMinMax: true,
      });
    }

    xAxis.get('renderer').labels.template.setAll({
      fill: am5.color('#f7f9f9'),
      fontSize: 12,
      fontFamily: 'Mulish',
    });
    let dynamicLogMin: number | undefined;
    let dynamicLogMax: number | undefined;

    if (isLogarithmic) {
      const visibleValues = OGL_SERIES_CONFIG.filter((c) => c.visible).flatMap((c) =>
        wellTestData.map((d) => (d as any)[c.field]).filter((v) => v > 0),
      );

      const minAcross = Math.min(...visibleValues);
      const maxAcross = Math.max(...visibleValues);

      const paddingFactor = 6;

      if (isFinite(minAcross)) {
        dynamicLogMin = Math.max(minAcross * (1 - paddingFactor), 0.001);
      }

      if (isFinite(maxAcross)) {
        dynamicLogMax = maxAcross * (1 + paddingFactor);
      }
    }

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: isLogarithmic ? dynamicLogMin : 0,
        max: isLogarithmic ? dynamicLogMax : undefined,
        logarithmic: isLogarithmic,
        renderer: isTestDashboard
          ? am5xy.AxisRendererY.new(root, {
              pan: 'zoom',
              strokeWidth: 1,
              strokeOpacity: 1,
              stroke: am5core.color('#4A5463'),
            })
          : am5xy.AxisRendererY.new(root, {
              minGridDistance: 20,
            }),
      }),
    );

    // Add Y-axis title when in test screen
    if (isTestDashboard) {
      yAxis.children.unshift(
        am5.Label.new(root, {
          rotation: -90,
          text: 'Oil, gas, water, total liquids',
          y: am5.percent(50),
          centerX: am5.percent(50),
          textAlign: 'center',
          oversizedBehavior: 'wrap',
          maxHeight: 500,
          fontFamily: 'Mulish',
          fontSize: 12,
          fontWeight: '400',
          marginRight: -5,
          minWidth: 45,
        }),
      );

      customizeAxisLabels(yAxis.get('renderer'), 14);
    }

    if (!isTestDashboard) {
      // y-axis label
      yAxis.get('renderer').labels.template.setAll({
        fill: am5.color('#f7f9f9'),
        fontSize: 12,
        fontFamily: 'Mulish',
        paddingRight: 10,
      });
    }

    // y-axis grid
    const gridSettings = {
      stroke: am5.color('#616a77'),
      strokeWidth: 1,
      strokeOpacity: 0.5,
      visible: true,
      strokeDasharray: isTestDashboard ? undefined : [4, 4], // Dotted effect
    };

    yAxis.get('renderer').grid.template.setAll(gridSettings);
    xAxis.get('renderer').grid.template.setAll(gridSettings);

    if (isTestDashboard) {
      // with cursor bullets and helper functions
      const allSeries: am5xy.LineSeries[] = [];
      const bullets: am5.Bullet[] = [];

      OGL_SERIES_CONFIG.forEach((config) => {
        const series = createSeries(config, chart, xAxis, yAxis, root);
        allSeries.push(series);

        const bullet = createCursorBullet(root, config.testColor);
        bullets.push(bullet);
        series.bulletsContainer.children.push(bullet.get('sprite'));
      });

      // Use helper functions for cursor setup
      const cursor = setupCursor(chart, root, xAxis, '#60BFDA', 2, yAxis);

      // Prepare chart data for the helper function
      const chartData = OGL_SERIES_CONFIG.map((config) => ({
        title: config.tooltipLabel,
        shortUnitOfMeasure: config.units,
      }));

      const parameterData = OGL_SERIES_CONFIG.map((config) => ({
        name: config.tooltipLabel,
        lineColor: config.testColor,
        markerColor: config.testColor,
      }));

      setupCursorEvents(cursor, xAxis, chart, allSeries, bullets, chartData, parameterData, 'line', {
        tooltipOffset: 20, // Move tooltip to the right for better visibility
      });

      // Set data for all series
      allSeries.forEach((series, index) => {
        const config = OGL_SERIES_CONFIG[index];
        if (isLogarithmic) {
          const cleanedData = wellTestData
            .filter((item) => (item as any)[config.field] > 0)
            .map((item) => ({
              ...item,
              [config.field]: (item as any)[config.field],
            }));

          series.data.setAll(cleanedData);
        } else {
          series.data.setAll(wellTestData);
        }
      });
    } else {
      //chart without cursor bullets
      const allSeries = OGL_SERIES_CONFIG.map((config) => createSeries(config, chart, xAxis, yAxis, root));

      const cursor = chart.set(
        'cursor',
        am5xy.XYCursor.new(root, {
          behavior: 'zoomX',
          xAxis: xAxis,
        }),
      );

      cursor.lineX.setAll({ stroke: am5.color('#60BFDA'), strokeWidth: 2 });
      cursor.lineY.setAll({ stroke: am5.color('#60BFDA'), strokeWidth: 2, visible: false });
      cursor.setAll({ xAxis: xAxis, yAxis: yAxis });

      // Set data for all series
      allSeries.forEach((series) => series.data.setAll(wellTestData));
    }

    const legend = createChartLegend(chart, root);

    legend.data.setAll(chart.series.values);

    legend.dataItems.forEach((dataItem, index) => {
      const series = dataItem.dataContext as am5xy.LineSeries;
      const config = OGL_SERIES_CONFIG[index];

      const hasData = series?.data?.values?.some((item: any) => {
        const value = item?.[config?.field];
        return value != null && !isNaN(Number(value));
      });

      if (hasData) {
        const color = isTestDashboard ? config.testColor : config.color;
        (series as any).setPrivate('legendMarkerFill', am5.color(color));
      }
    });

    // Make stuff animate on load
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [wellTestData, isTestDashboard, chartViewOptions, showTooltip]);

  if (wellTestDataLoading) {
    return <Loader />;
  }

  return (
    <section
      ref={containerRef}
      className={`ogl-chart-container ${isFullscreen ? 'fullscreen' : ''}`}
      style={isTestDashboard && !isFullscreen ? { background: 'transparent' } : undefined}
    >
      <header className='ogl-chart-header'>
        <div className='chart-title'>
          <h2>Oil, gas, water, total liquids</h2>
        </div>
        <div className='chart-actions'>
          <ProtectAction canAccess={rPermission.canExpand}>
            <button analytics-id='ogl-chart-fullscreen-btn' className='chart-action-button' onClick={toggleFullscreen}>
              <FullScreenIcon />
            </button>
          </ProtectAction>
          <button analytics-id='ogl-chart-download-btn' className='chart-action-button'>
            <DownloadChart
              onClick={(option) => handleChartDownload(option)}
              canExportJpg={rPermission.canExportJpg}
              canExportPdf={rPermission.canExportPdf}
            />
          </button>
        </div>
      </header>

      {(!isTestDashboard || isFullscreen) && <div className='chart-divider'></div>}

      {wellTestData.length > 0 ? (
        <div id='ogl-chart-content'>
          <div ref={chartRef} className='chart-area'></div>
        </div>
      ) : (
        <EmptyState message='No data available for the selected date range. Please adjust your selection and try again' />
      )}
    </section>
  );
};

export default React.memo(OilGasChart);
